import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const INTEGRATIONS = [
  { key: 'anthropic', label: 'Anthropic', description: 'Powers ICP generation, sequence writing and intelligence synthesis.', envKey: 'ANTHROPIC_API_KEY', required: true },
  { key: 'manyreach', label: 'ManyReach', description: 'Pushes approved sequences directly into campaigns.', envKey: 'MANYREACH_API_KEY', required: true },
  { key: 'apollo', label: 'Apollo.io', description: 'Company and contact search for prospect research.', envKey: 'APOLLO_API_KEY', required: false },
  { key: 'openai', label: 'OpenAI', description: 'Secondary AI tasks and research synthesis.', envKey: 'OPENAI_API_KEY', required: false },
  { key: 'resend', label: 'Resend', description: 'Intelligence digest delivery to clients.', envKey: 'RESEND_API_KEY', required: false },
];


function Settings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState(false);
  const [newKeys, setNewKeys] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});

  useEffect(() => { fetchSettings(); }, []);

  async function fetchSettings() {
    try {
      const res = await fetch(`${API_BASE}/settings`);
      setSettings(await res.json());
    } catch (err) {}
  }

  async function handleTest() {
    setTesting(true);
    try {
      const res = await fetch(`${API_BASE}/settings/test`);
      setTestResults(await res.json());
    } catch (err) {}
    setTesting(false);
  }

  async function handleSaveKey(key, envKey) {
    const value = newKeys[key];
    if (!value?.trim()) return;
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${API_BASE}/settings/key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ envKey, value })
      });
      if (res.ok) {
        setSaved(prev => ({ ...prev, [key]: true }));
        setNewKeys(prev => ({ ...prev, [key]: '' }));
        await fetchSettings();
        setTimeout(() => setSaved(prev => ({ ...prev, [key]: false })), 3000);
      }
    } catch (err) {}
    setSaving(prev => ({ ...prev, [key]: false }));
  }

  const statusColour = (status) => {
    if (status === 'connected') return '#276749';
    if (status === 'error') return '#C53030';
    return '#888';
  };

  const statusBg = (status) => {
    if (status === 'connected') return '#F0FFF4';
    if (status === 'error') return '#FFF5F5';
    return '#F7FAFC';
  };

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      <div style={{ padding: '2rem', maxWidth: '720px', margin: '0 auto', boxSizing: 'border-box' }}>

        <button onClick={() => navigate('/clients')} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>
          ← All clients
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.25rem', color: '#1A1A1A' }}>Settings</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>API keys and integrations</p>
          </div>
          <button onClick={handleTest} disabled={testing} style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', opacity: testing ? 0.5 : 1 }}>
            {testing ? 'Testing...' : 'Test all connections'}
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {INTEGRATIONS.map(integration => (
            <div key={integration.key} style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <p style={{ margin: 0, fontWeight: '500', fontSize: '0.95rem', color: '#1A1A1A' }}>{integration.label}</p>
                    {integration.required && <span style={{ fontSize: '0.68rem', color: '#8B1A1A', backgroundColor: '#F5E6E6', padding: '0.1rem 0.4rem', borderRadius: '20px', fontWeight: '500' }}>Required</span>}
                  </div>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>{integration.description}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: '1rem' }}>
                  {settings[integration.key] && (
                    <span style={{ fontSize: '0.78rem', color: '#888', fontFamily: 'monospace' }}>{settings[integration.key]}</span>
                  )}
                  {testResults[integration.key] && (
                    <span style={{ fontSize: '0.72rem', color: statusColour(testResults[integration.key]), backgroundColor: statusBg(testResults[integration.key]), padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '500' }}>
                      {testResults[integration.key]}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <input
                  type="password"
                  value={newKeys[integration.key] || ''}
                  onChange={e => setNewKeys(prev => ({ ...prev, [integration.key]: e.target.value }))}
                  placeholder={settings[integration.key] ? 'Update key...' : 'Enter API key...'}
                  style={{ flex: 1, backgroundColor: '#FAFAF8', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', color: '#1A1A1A', fontSize: '0.85rem', fontFamily: 'monospace', outline: 'none' }}
                />
                <button
                  onClick={() => handleSaveKey(integration.key, integration.envKey)}
                  disabled={saving[integration.key] || !newKeys[integration.key]?.trim()}
                  style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', opacity: saving[integration.key] || !newKeys[integration.key]?.trim() ? 0.5 : 1, whiteSpace: 'nowrap', fontWeight: '500' }}
                >
                  {saving[integration.key] ? 'Saving...' : saved[integration.key] ? 'Saved' : 'Save key'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '1.5rem', padding: '1.25rem', backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px' }}>
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', fontWeight: '500', color: '#1A1A1A' }}>Note</p>
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#888', lineHeight: '1.6' }}>API keys are stored in the server environment and never exposed in the browser. To update a key, enter the new value above and click Save. Changes take effect immediately without a server restart.</p>
        </div>
      </div>
    </div>
  );
}

export default Settings;
